import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  Box, Button, Chip, CircularProgress, TextField, Typography,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import { searchMerchandisingValues } from '../state/merchandisingSlice';
import { SearchInput, SelectionCheckbox } from './MerchandisingWorkbenchUi';

const normalizeIds = (value, multiple) => {
  if (multiple) return (Array.isArray(value) ? value : []).map(String);
  return value == null || value === '' ? [] : [String(value)];
};

/**
 * Descriptor-driven bridge selector. It deliberately owns request sequencing:
 * one physical request at a time, with only the newest queued search executed.
 */
const SchemaValueSelector = ({
  action,
  field = null,
  languageId = null,
  value,
  multiple = false,
  onChange,
  onItems = null,
  presentation = 'default',
}) => {
  const dispatch = useDispatch();
  const t = useLocalizedMessage();
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const selected = useMemo(() => normalizeIds(value, multiple), [multiple, value]);
  const literalValues = action === 'merchandising.condition_values.search';
  const fallbackLabel = id => (literalValues ? String(id) : `#${id}`);
  const mounted = useRef(true);
  const inFlight = useRef(false);
  const queued = useRef(null);
  const generation = useRef(0);

  useEffect(() => () => { mounted.current = false; }, []);

  const request = async ({ search, cursor = null, append = false }) => {
    const ownGeneration = ++generation.current;
    inFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      const result = await dispatch(searchMerchandisingValues({
        action,
        field,
        query: search,
        languageId,
        cursor,
        limit: 20,
      }));
      if (!searchMerchandisingValues.fulfilled.match(result)) {
        const requestError = new Error(result.payload?.message || t('merchandising.error.generic'));
        requestError.code = result.payload?.code;
        throw requestError;
      }
      const response = result.payload;
      if (mounted.current && ownGeneration === generation.current) {
        setItems((current) => append ? [...current, ...(response.items || [])] : (response.items || []));
        setNextCursor(response.next_cursor || null);
        if (onItems) onItems(response.items || []);
      }
    } catch (requestError) {
      if (mounted.current && ownGeneration === generation.current) setError(requestError);
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
      const pending = queued.current;
      queued.current = null;
      if (pending && mounted.current) request(pending);
    }
  };

  const schedule = (next) => {
    if (inFlight.current) {
      queued.current = next;
      generation.current += 1;
      return;
    }
    request(next);
  };

  useEffect(() => {
    const timeout = setTimeout(() => schedule({ search: query, cursor: null, append: false }), 250);
    return () => clearTimeout(timeout);
  }, [action, field, languageId, query]);

  const pick = (id) => {
    const normalized = String(id);
    if (multiple) {
      if (!selected.includes(normalized)) onChange([...selected, normalized]);
    } else {
      onChange(normalized);
    }
  };
  const remove = (id) => {
    if (multiple) onChange(selected.filter((entry) => entry !== id));
    else onChange('');
  };

  if (presentation === 'compact') {
    const more = { id: null, label: t('merchandising.action.loadMore'), loadMore: true };
    const selectedItems = selected.map(id => items.find(item => String(item.id) === id)
      || { id, label: fallbackLabel(id) });
    return (
      <div className="mp-compact-value-selector">
        <Autocomplete
          multiple={multiple}
          disableCloseOnSelect={multiple}
          options={nextCursor ? [...items, more] : items}
          value={multiple ? selectedItems : selectedItems[0] || null}
          inputValue={query}
          onInputChange={(_event, next, reason) => {
            if (reason === 'input' || reason === 'clear') setQuery(next);
          }}
          filterOptions={options => options}
          getOptionLabel={item => item.label || item.breadcrumb || fallbackLabel(item.id)}
          getOptionSelected={(left, right) => String(left.id) === String(right.id)}
          loading={loading}
          noOptionsText={t('merchandising.selector.empty')}
          classes={{ paper: 'mp-rule-select-menu', option: 'mp-compact-value-option' }}
          onChange={(_event, next) => {
            const chosen = multiple ? next : next ? [next] : [];
            if (chosen.some(item => item.loadMore)) {
              schedule({ search: query, cursor: nextCursor, append: true });
            } else onChange(multiple ? chosen.map(item => String(item.id)) : chosen[0]?.id || '');
          }}
          renderOption={(item, state) => item.loadMore ? item.label : (
            <><SelectionCheckbox checked={state.selected} label={item.label || fallbackLabel(item.id)} onChange={() => {}} /><span>{item.label || item.breadcrumb || fallbackLabel(item.id)}</span></>
          )}
          renderInput={params => <TextField {...params} label={t('merchandising.selector.search')} variant="outlined" size="small" />}
        />
        {error && <div className="mp-inline-error" role="alert"><span>{error.message}</span><Button onClick={() => schedule({ search: query, cursor: null, append: false })}>{t('merchandising.action.retry')}</Button></div>}
      </div>
    );
  }

  if (presentation === 'workbench') {
    const toggle = (id) => {
      const normalized = String(id);
      if (selected.includes(normalized)) remove(normalized);
      else pick(normalized);
    };

    return (
      <div className="mp-workbench-value-selector">
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder={t('merchandising.selector.search')}
        />
        {error && (
          <div className="mp-inline-error" role="alert">
            <span>{error.message || t('merchandising.error.generic')}</span>
            <Button onClick={() => schedule({ search: query, cursor: null, append: false })}>
              {t('merchandising.action.retry')}
            </Button>
          </div>
        )}
        <div
          className="mp-value-picker"
          role="group"
          aria-label={t('merchandising.selector.results')}
          aria-busy={loading}
        >
          {items.map((item) => {
            const id = String(item.id);
            const isSelected = selected.includes(id);
            return (
              <label key={id} className={`mp-value-option ${isSelected ? 'is-selected' : ''}`}>
                <SelectionCheckbox
                  checked={isSelected}
                  label={item.label || item.breadcrumb || fallbackLabel(id)}
                  onChange={() => toggle(id)}
                />
                <span className="mp-value-option-copy">
                  <strong>{item.label || item.breadcrumb || fallbackLabel(id)}</strong>
                  {(item.breadcrumb || Number.isFinite(item.count)) && (
                    <small>{item.breadcrumb || t('merchandising.selector.productsCount', { count: item.count })}</small>
                  )}
                </span>
              </label>
            );
          })}
          {!loading && items.length === 0 && (
            <div className="mp-value-picker-empty">{t('merchandising.selector.empty')}</div>
          )}
        </div>
        {nextCursor && (
          <Button
            className="mp-value-picker-more"
            variant="outlined"
            disabled={loading}
            onClick={() => schedule({ search: query, cursor: nextCursor, append: true })}
          >
            {t('merchandising.action.loadMore')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <Box minWidth={280}>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        label={t('merchandising.selector.search')}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        inputProps={{ 'aria-label': t('merchandising.selector.search') }}
        InputProps={{
          endAdornment: loading ? <CircularProgress size={18} /> : null,
        }}
      />
      {error && (
        <Alert
          severity="error"
          action={(
            <Button
              color="inherit"
              size="small"
              onClick={() => schedule({ search: query, cursor: null, append: false })}
            >
              {t('merchandising.action.retry')}
            </Button>
          )}
        >
          {error.message || t('merchandising.error.generic')}
        </Alert>
      )}
      <Box display="flex" flexWrap="wrap" style={{ gap: 4 }} my={1}>
        {selected.map((id) => (
          <Chip
            key={id}
            size="small"
            label={items.find((item) => String(item.id) === id)?.label || fallbackLabel(id)}
            onDelete={() => remove(id)}
          />
        ))}
      </Box>
      <Box
        role="listbox"
        aria-label={t('merchandising.selector.results')}
        style={{ maxHeight: 160, overflowY: 'auto' }}
      >
        {items.map((item) => (
          <Button
            fullWidth
            key={String(item.id)}
            size="small"
            role="option"
            aria-selected={selected.includes(String(item.id))}
            disabled={selected.includes(String(item.id))}
            onClick={() => pick(item.id)}
            style={{ justifyContent: 'flex-start' }}
          >
            {item.label || item.breadcrumb || fallbackLabel(item.id)}
          </Button>
        ))}
        {!loading && items.length === 0 && (
          <Typography variant="caption">{t('merchandising.selector.empty')}</Typography>
        )}
      </Box>
      {nextCursor && (
        <Button
          size="small"
          disabled={loading}
          onClick={() => schedule({ search: query, cursor: nextCursor, append: true })}
        >
          {t('merchandising.action.loadMore')}
        </Button>
      )}
    </Box>
  );
};

export default SchemaValueSelector;
